import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Clock,
  Eye,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  Store,
  Truck,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { socket } from '../chat/socket';
import { getAlertDescription, getAlertLabel } from '../../delivery-alerts/alertTypes';

const API = process.env.REACT_APP_API_URL || '';

function authConfig(extra = {}) {
  const token = window.localStorage.getItem('token');
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

const STATUS_TABS = [
  { id: 'open', label: 'Activas', status: 'open', icon: AlertTriangle },
  { id: 'acknowledged', label: 'En gestion', status: 'acknowledged', icon: Clock },
  { id: 'resolved', label: 'Resueltas', status: 'resolved', icon: CheckCircle2 },
  { id: 'dismissed', label: 'Descartadas', status: 'dismissed', icon: XCircle },
  { id: 'all', label: 'Todas', status: 'all', icon: ShieldCheck },
];

const STATUS_LABELS = {
  open: 'Activa',
  acknowledged: 'En gestion',
  resolved: 'Resuelta',
  dismissed: 'Descartada',
};

const SEVERITY_LABELS = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('es-PE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (_) {
    return '-';
  }
}

function severityClasses(severity) {
  if (severity === 'high') {
    return {
      badge: 'bg-red-100 text-red-700',
      marker: 'bg-red-500',
      border: 'border-red-200',
      icon: 'bg-red-50 text-red-600',
    };
  }
  if (severity === 'low') {
    return {
      badge: 'bg-slate-100 text-slate-700',
      marker: 'bg-slate-400',
      border: 'border-slate-200',
      icon: 'bg-slate-50 text-slate-600',
    };
  }
  return {
    badge: 'bg-amber-100 text-amber-700',
    marker: 'bg-amber-500',
    border: 'border-amber-200',
    icon: 'bg-amber-50 text-amber-600',
  };
}

function statusClasses(status) {
  if (status === 'resolved') return 'bg-green-100 text-green-700';
  if (status === 'dismissed') return 'bg-slate-100 text-slate-600';
  if (status === 'acknowledged') return 'bg-blue-100 text-blue-700';
  return 'bg-red-100 text-red-700';
}

function upsertAlert(alerts, incoming) {
  const exists = alerts.some(alert => alert.id === incoming.id);
  if (exists) {
    return alerts.map(alert => alert.id === incoming.id ? incoming : alert);
  }
  return [incoming, ...alerts];
}

function normalizeText(value) {
  return String(value || '').toLowerCase();
}

function InfoLine({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
        <div className="text-sm font-semibold text-slate-800 break-words">{value || '-'}</div>
      </div>
    </div>
  );

  if (!href || !value) return content;
  return <a href={href} className="block hover:opacity-80">{content}</a>;
}

function AlertDetailModal({ alert, onClose, onAction, updating }) {
  if (!alert) return null;

  const delivery = alert.delivery || {};
  const severity = severityClasses(alert.severity);
  const canAcknowledge = alert.status === 'open';
  const canClose = alert.status === 'open' || alert.status === 'acknowledged';

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
      <button className="absolute inset-0 bg-slate-900/50" onClick={onClose} aria-label="Cerrar" />
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${severity.icon}`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 font-montserrat truncate">
                {getAlertLabel(alert.alert_type)}
              </h2>
              <p className="text-xs text-slate-500">
                Entrega #{alert.delivery_point_id}
                {delivery.sequence != null ? ` · Secuencia ${delivery.sequence}` : ''}
                {' - '}
                {formatDateTime(alert.created_at)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <div className="flex flex-wrap gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${severity.badge}`}>
              Severidad {SEVERITY_LABELS[alert.severity] || alert.severity}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusClasses(alert.status)}`}>
              {STATUS_LABELS[alert.status] || alert.status}
            </span>
          </div>

          <div className={`rounded-lg border ${severity.border} bg-slate-50 p-4`}>
            <p className="text-sm text-slate-700">{getAlertDescription(alert.alert_type)}</p>
            {alert.message && (
              <p className="mt-3 text-sm font-semibold text-slate-900">{alert.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoLine icon={Truck} label="Conductor" value={alert.driver?.username} />
            <InfoLine icon={Phone} label="Telefono conductor" value={alert.driver?.phone_number} href={alert.driver?.phone_number ? `tel:${alert.driver.phone_number}` : null} />
            <InfoLine icon={Store} label="Tienda / Usuario" value={delivery.user_name || alert.user?.username} />
            <InfoLine icon={Clock} label="Secuencia" value={delivery.sequence != null ? delivery.sequence : '-'} />
            <InfoLine icon={User} label="Cliente final" value={delivery.recipient_name} />
            <InfoLine icon={Phone} label="Telefono cliente" value={delivery.recipient_phone} href={delivery.recipient_phone ? `tel:${delivery.recipient_phone}` : null} />
            <InfoLine icon={Clock} label="Estado entrega" value={delivery.status} />
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Direccion</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {[delivery.address, delivery.district].filter(Boolean).join(', ') || '-'}
            </div>
            {delivery.charge != null && (
              <div className="mt-2 text-sm text-slate-600">
                Cobro registrado: <span className="font-bold text-slate-900">S/ {Number(delivery.charge || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <button
            onClick={() => onAction(alert, 'acknowledge')}
            disabled={updating || !canAcknowledge}
            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            En gestion
          </button>
          <button
            onClick={() => onAction(alert, 'resolve')}
            disabled={updating || !canClose}
            className="rounded-lg bg-brand-green px-3 py-2 text-sm font-semibold text-white hover:bg-brand-green/90 disabled:opacity-50"
          >
            Resolver
          </button>
          <button
            onClick={() => onAction(alert, 'dismiss')}
            disabled={updating || !canClose}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertRow({ alert, onSelect, onAction, updating }) {
  const delivery = alert.delivery || {};
  const severity = severityClasses(alert.severity);
  const canAcknowledge = alert.status === 'open';
  const canClose = alert.status === 'open' || alert.status === 'acknowledged';

  return (
    <div className={`relative bg-white border ${severity.border} rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${severity.marker}`} />
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 pl-2">
        <button onClick={() => onSelect(alert)} className="flex-1 text-left min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-bold text-slate-900 font-montserrat">{getAlertLabel(alert.alert_type)}</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${severity.badge}`}>
              {SEVERITY_LABELS[alert.severity] || alert.severity}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusClasses(alert.status)}`}>
              {STATUS_LABELS[alert.status] || alert.status}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div className="min-w-0">
              <div className="text-xs text-slate-400">Entrega</div>
              <div className="font-semibold text-slate-800 truncate">
                #{alert.delivery_point_id}
                {delivery.sequence != null ? ` · Seq ${delivery.sequence}` : ''}
                {' - '}
                {delivery.recipient_name || 'Cliente'}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-slate-400">Tienda</div>
              <div className="font-semibold text-slate-800 truncate">{delivery.user_name || alert.user?.username || '-'}</div>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-slate-400">Conductor</div>
              <div className="font-semibold text-slate-800 truncate">{alert.driver?.username || '-'}</div>
            </div>
          </div>

          <div className="mt-2 text-xs text-slate-500 truncate">
            {[delivery.address, delivery.district].filter(Boolean).join(', ') || 'Sin direccion'}
          </div>
          {alert.message && (
            <div className="mt-2 text-sm text-slate-700 line-clamp-2">{alert.message}</div>
          )}
          <div className="mt-2 text-xs text-slate-400">{formatDateTime(alert.created_at)}</div>
        </button>

        <div className="flex lg:flex-col gap-2">
          <button
            onClick={() => onSelect(alert)}
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Eye className="w-3.5 h-3.5" />
            Ver
          </button>
          {canAcknowledge && (
            <button
              onClick={() => onAction(alert, 'acknowledge')}
              disabled={updating}
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              Gestion
            </button>
          )}
          {canClose && (
            <button
              onClick={() => onAction(alert, 'resolve')}
              disabled={updating}
              className="rounded-lg bg-brand-green px-3 py-2 text-xs font-semibold text-white hover:bg-brand-green/90 disabled:opacity-50"
            >
              Resolver
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDeliveryAlertsDashboard({ userId, role = 'admin', setActiveComponent }) {
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('open');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/delivery-alerts`, {
        params: {
          role,
          user_id: userId,
          status: 'all',
          limit: 300,
        },
        ...authConfig(),
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error loading delivery alerts dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [role, userId]);

  useEffect(() => {
    if (!userId) return;
    fetchAlerts();
  }, [fetchAlerts, userId]);

  useEffect(() => {
    if (!userId) return;

    const joinPayload = { role, user_id: userId };
    socket.emit('join_delivery_alerts', joinPayload);

    const handleAlert = (alert) => {
      setAlerts(prev => upsertAlert(prev, alert));
      setSelectedAlert(prev => prev?.id === alert.id ? alert : prev);
    };
    const handleConnect = () => socket.emit('join_delivery_alerts', joinPayload);

    socket.on('delivery_alert_created', handleAlert);
    socket.on('delivery_alert_updated', handleAlert);
    socket.on('connect', handleConnect);

    return () => {
      socket.off('delivery_alert_created', handleAlert);
      socket.off('delivery_alert_updated', handleAlert);
      socket.off('connect', handleConnect);
      socket.emit('leave_delivery_alerts', joinPayload);
    };
  }, [role, userId]);

  const visibleAlerts = useMemo(() => {
    const tab = STATUS_TABS.find(item => item.id === activeTab);
    const status = tab?.status;
    const filteredByTab = status === 'all'
      ? alerts
      : alerts.filter(alert => alert.status === status);

    const term = normalizeText(search.trim());
    if (!term) return filteredByTab;

    return filteredByTab.filter(alert => {
      const delivery = alert.delivery || {};
      return [
        alert.id,
        alert.delivery_point_id,
        delivery.sequence,
        getAlertLabel(alert.alert_type),
        alert.message,
        alert.driver?.username,
        alert.driver?.phone_number,
        alert.user?.username,
        delivery.user_name,
        delivery.recipient_name,
        delivery.recipient_phone,
        delivery.address,
        delivery.district,
      ].some(value => normalizeText(value).includes(term));
    });
  }, [activeTab, alerts, search]);

  const counts = useMemo(() => {
    return alerts.reduce((acc, alert) => {
      acc[alert.status] = (acc[alert.status] || 0) + 1;
      acc.all += 1;
      return acc;
    }, { open: 0, acknowledged: 0, resolved: 0, dismissed: 0, all: 0 });
  }, [alerts]);

  const handleAction = async (alert, action) => {
    try {
      setUpdating(true);
      const response = await axios.patch(`${API}/api/delivery-alerts/${alert.id}`, {
        action,
        user_id: userId,
      }, authConfig());
      const updated = response.data.alert;
      setAlerts(prev => upsertAlert(prev, updated));
      setSelectedAlert(prev => prev?.id === updated.id ? updated : prev);
    } catch (error) {
      console.error('Error updating delivery alert:', error);
      window.alert('No se pudo actualizar la alerta.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              title="Volver"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 font-montserrat">Alertas de Entrega</h1>
              <p className="text-sm text-slate-500">Gestion de incidencias reportadas por conductores</p>
            </div>
          </div>
          <button
            onClick={fetchAlerts}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {STATUS_TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                  active ? 'border-brand-primary bg-brand-primary text-white' : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-800'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Icon className="w-5 h-5" />
                  <span className={`text-lg font-extrabold ${active ? 'text-white' : 'text-slate-900'}`}>
                    {counts[tab.id] || 0}
                  </span>
                </div>
                <div className="mt-2 text-sm font-semibold">{tab.label}</div>
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por entrega, cliente, tienda, conductor, direccion..."
              className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>
        </div>

        <div className="space-y-3">
          {loading && visibleAlerts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
              Cargando alertas...
            </div>
          ) : visibleAlerts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-brand-green mb-2" />
              <div className="font-bold text-slate-900">No hay alertas en esta vista</div>
              <div className="text-sm text-slate-500 mt-1">Cambia de filtro o espera nuevas incidencias de conductores.</div>
            </div>
          ) : (
            visibleAlerts.map(alert => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onSelect={setSelectedAlert}
                onAction={handleAction}
                updating={updating}
              />
            ))
          )}
        </div>
      </div>

      <AlertDetailModal
        alert={selectedAlert}
        onClose={() => setSelectedAlert(null)}
        onAction={handleAction}
        updating={updating}
      />
    </div>
  );
}
